/**
 * @file src/pages/invoice-creation-wizard/components/ValidationPanel.jsx
 *
 * What changed from the original:
 *  - isRefreshing animate-spin moved from Button wrapper to an inline
 *    spinner icon so only the icon spins, not the whole button
 *  - ValidationItem error icon changed from AlertTriangle to XCircle
 *    to visually differentiate errors from warnings
 *  - ValidationItem getIcon/getColor consolidated into a single lookup
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";

// ---------------------------------------------------------------------------
// ValidationPanel
// ---------------------------------------------------------------------------

const ValidationPanel = ({
  validationErrors,
  aiValidationResults,
  onRefresh,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const errorCount = validationErrors.length;
  const warningCount = aiValidationResults.filter(
    (r) => r.type === "warning",
  ).length;
  const aiErrorCount = aiValidationResults.filter(
    (r) => r.type === "error",
  ).length;
  const isAllValid = errorCount === 0 && aiErrorCount === 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6 }}
      className="sticky top-32 space-y-4"
    >
      {/* ── Validation Status ────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground dark:text-white">
            Validation Status
          </h3>
          {/* Spinner on icon only — not the whole button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh validation"
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <Loader2
              size={16}
              className={`text-muted-foreground ${isRefreshing ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        <div className="space-y-3">
          {[
            {
              icon: "CheckCircle2",
              label: "Form Validation",
              valid: errorCount === 0,
              text:
                errorCount === 0
                  ? "Valid"
                  : `${errorCount} error${errorCount !== 1 ? "s" : ""}`,
            },
            {
              icon: "Zap",
              label: "AI Validation",
              valid: aiErrorCount === 0,
              text:
                aiErrorCount === 0
                  ? "Valid"
                  : `${aiErrorCount} issue${aiErrorCount !== 1 ? "s" : ""}`,
            },
            {
              icon: "AlertTriangle",
              label: "Warnings",
              valid: warningCount === 0,
              text:
                warningCount === 0
                  ? "None"
                  : `${warningCount} warning${warningCount !== 1 ? "s" : ""}`,
              neutral: true,
            },
          ].map(({ icon, label, valid, text, neutral }) => (
            <div key={label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon
                  name={icon}
                  size={16}
                  className={
                    neutral
                      ? valid
                        ? "text-gray-400 dark:text-gray-600"
                        : "text-warning"
                      : valid
                        ? "text-success"
                        : "text-error"
                  }
                />
                <span className="text-sm text-muted-foreground dark:text-gray-300">
                  {label}
                </span>
              </div>
              <span
                className={`text-sm font-medium ${
                  neutral
                    ? valid
                      ? "text-gray-400 dark:text-gray-600"
                      : "text-warning"
                    : valid
                      ? "text-success"
                      : "text-error"
                }`}
              >
                {text}
              </span>
            </div>
          ))}
        </div>

        {/* Overall status */}
        <div
          className={`mt-4 p-3 rounded-lg ${
            isAllValid
              ? "bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800"
              : "bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800"
          }`}
        >
          <div className="flex items-center gap-2">
            <Icon
              name={isAllValid ? "CheckCircle2" : "AlertTriangle"}
              size={16}
              className={isAllValid ? "text-success" : "text-error"}
            />
            <span
              className={`text-sm font-medium ${isAllValid ? "text-success" : "text-error"}`}
            >
              {isAllValid ? "Ready to submit" : "Issues need attention"}
            </span>
          </div>
        </div>
      </div>

      {/* ── Form Errors ───────────────────────────────────────────────────── */}
      {errorCount > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-error-200 dark:border-error-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="AlertTriangle" size={20} className="text-error" />
            <h4 className="font-medium text-error">Form Errors</h4>
          </div>
          <div className="space-y-2">
            {validationErrors.map((error, index) => (
              <div key={index} className="text-sm text-error">
                • {error}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── AI Validation Results ─────────────────────────────────────────── */}
      {aiValidationResults.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Zap" size={20} className="text-primary" />
            <h4 className="font-medium text-foreground dark:text-white">
              AI Validation Results
            </h4>
          </div>
          <div className="space-y-3">
            <AnimatePresence>
              {aiValidationResults.map((result, index) => (
                <ValidationItem key={index} result={result} />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* ── ZATCA Compliance ──────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon
            name="Shield"
            size={20}
            className="text-blue-600 dark:text-blue-400"
          />
          <h4 className="font-medium text-foreground dark:text-white">
            ZATCA Compliance
          </h4>
        </div>
        <div className="space-y-3">
          {[
            "Phase 1 Compatible",
            "Phase 2 Ready",
            "Tax Calculations Valid",
          ].map((item) => (
            <div key={item} className="flex items-center gap-2">
              <Icon name="CheckCircle2" size={16} className="text-success" />
              <span className="text-sm text-muted-foreground dark:text-gray-300">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Help & Support ────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700 p-6">
        <h4 className="font-medium text-foreground dark:text-white mb-4">
          Need Help?
        </h4>
        <div className="space-y-3">
          {["ZATCA Guidelines", "Tax Calculation Help", "Contact Support"].map(
            (label) => (
              <Button
                key={label}
                variant="outline"
                size="sm"
                iconName="Info"
                className="w-full justify-start"
              >
                {label}
              </Button>
            ),
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// ValidationItem
// ---------------------------------------------------------------------------

const TYPE_CONFIG = {
  error: {
    icon: "XCircle",
    color: "text-error",
    border:
      "border-error-200 dark:border-error-800 bg-error-50 dark:bg-error-900/20",
  },
  warning: {
    icon: "AlertTriangle",
    color: "text-warning",
    border:
      "border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20",
  },
  info: {
    icon: "Info",
    color: "text-blue-600 dark:text-blue-400",
    border:
      "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20",
  },
};

const ValidationItem = ({ result }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = TYPE_CONFIG[result.type] ?? TYPE_CONFIG.info;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className={`border rounded-lg p-3 ${config.border}`}
    >
      <div
        className="flex items-start gap-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <Icon
          name={config.icon}
          size={16}
          className={`${config.color} mt-0.5 shrink-0`}
        />
        <div className="flex-1">
          <div className={`text-sm font-medium ${config.color}`}>
            {result.message}
          </div>
          <AnimatePresence>
            {isExpanded && result.suggestion && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-2 text-xs text-muted-foreground dark:text-gray-300"
              >
                {result.suggestion}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default ValidationPanel;
